import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {HtmlToPdfRequest} from '@dave/types/dist/proto/htmlToPdf/htmlToPdf';
import {Store} from '@ngrx/store';
import { combineLatest, firstValueFrom, of, ReplaySubject, startWith, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, first, map, mergeMap, switchMap, switchMapTo, tap } from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {
    LedgerImportDocumentTypes,
    LedgerImportEntity,
    LedgerImportEntityFromBackend,
    LedgerImportEntityFromBackendGrpc,
    LedgerImportPaymentItem,
    LedgerImportPaymentItemFromString,
} from '../entities/ledger-import.entity';
import {State} from '../State';
import {BaseActionTypes} from '../State/actions/base.actions';
import {LedgerImportEffectErrorCodes} from '../State/effects/ledger-import.effects';
import {getToken} from '../State/selectors/base.selectors';
import { AppGatewayResponseHasError, AppGatewayService } from './app-gateway.service';
import {HttpService} from './http.service';
import {useGzip} from '../helper/backend-frontend-conversion.helper';
import {GetTimestampFromTime, IsConsolidatedInvoiceIdAvailableRequest} from '@dave/types';
import {FilterTypes} from "../../services/default-filter.service";
import {
    IFilterTypeSearchMultiSelectValue,
    IFilterTypeTimespanValue
} from "../../dave-utils-module/app-filter-module/app-filter/app-filter.component";
import {
    LedgerImportGRPCRequest,
    LedgerImportSortEnum,
    SortDirectionEnum
} from "@dave/types/dist/proto/accounting/ledgerImport";
import {isNotNullOrUndefined} from "../../helper/helper";
import { LedgerImportActionTypes } from '../State/actions/accounting.actions';
import { Actions, ofType } from '@ngrx/effects';
import { getLedgerImportById, getLedgerImportDictionary } from '../State/selectors/ledger-import.selector';

const MAX_ENTITY_LIMIT = 1000;
export type ledgerImportBackendFilter = {
    [FilterTypes.Type]: IFilterTypeSearchMultiSelectValue<LedgerImportDocumentTypes>[];
    [FilterTypes.Status]: IFilterTypeSearchMultiSelectValue<string>[];
    [FilterTypes.AssignedUser]: IFilterTypeSearchMultiSelectValue<number>[];
    [FilterTypes.AssignedCommission]: IFilterTypeSearchMultiSelectValue<number>[];
    [FilterTypes.Date]: IFilterTypeTimespanValue;
    [FilterTypes.Customers]: IFilterTypeSearchMultiSelectValue<number>[];
};

@Injectable({
    providedIn: 'root',
})
export class LedgerImportDataService {

    constructor(
        private gatewayServiceV2: AppGatewayService,
        private gatewayHttpService: HttpService,
        private store: Store<State>,
        private httpService: HttpService,
        private http: HttpClient,
    ) {
    }
    selectById$(id: number) {
        return this.store.select(getLedgerImportById({id})).pipe(
            distinctUntilChanged((a, b) => a?.UpdatedAt.getTime() === b?.UpdatedAt.getTime()),
        );
    }
    selectByIds$(ids: number[]) {
        return combineLatest(ids.map(id => this.selectById$(id)));
    }

    getLedgerImportPage(
        searchQuery: string,
        pageSize: number,
        lastId?: number,
        lastSortByValue?: string,
        sortDirection?: SortDirectionEnum,
        sortProperty?: LedgerImportSortEnum,
        filterObject?: Partial<ledgerImportBackendFilter>,
    ): Promise<{
        ledgerImports: LedgerImportEntity[];
        nextLastId?: number;
        nextSortByValue?: string;
        pageCount?: number;
    }> {
        return new Promise((resolve, reject) => {
            let req = {
                Limit: pageSize.toString(),
                LastSortByValue: lastSortByValue,
            } as LedgerImportGRPCRequest;

            if (filterObject && filterObject[FilterTypes.AssignedCommission]) {
                req.CommissionIds = filterObject[FilterTypes.AssignedCommission].map(id => id.id.toString()) || []
            }
            if (filterObject && filterObject[FilterTypes.Customers]) {
                req.CustomerIds = filterObject[FilterTypes.Customers].map(id => id.id.toString()) || []
            }

            if (filterObject && filterObject[FilterTypes.Status]) {
                req.DocumentStatus = filterObject[FilterTypes.Status].map(id => id.id.toString()) || []
            }

            if (filterObject && filterObject[FilterTypes.AssignedUser]) {
                req.UserIds = filterObject[FilterTypes.AssignedUser].map(id => id.id.toString()) || []
            }

            if (filterObject && filterObject[FilterTypes.Type]) {
                req.DocumentTypes = filterObject[FilterTypes.Type].map(id => id.id.toString()) || []
            }

            //TODO
            if (filterObject && isNotNullOrUndefined(filterObject[FilterTypes.Date])) {
                if (isNotNullOrUndefined(filterObject[FilterTypes.Date].from)) {
                    req.DateFrom = GetTimestampFromTime(filterObject[FilterTypes.Date].from.toDate()).toString();
                }
                if (isNotNullOrUndefined(filterObject[FilterTypes.Date].to)) {
                    req.DateTo = GetTimestampFromTime(filterObject[FilterTypes.Date].to.toDate()).toString();
                }
            }

            if (searchQuery !== "") {
                req.SearchQuery = searchQuery;
            }

            if (isNotNullOrUndefined(lastId)) {
                req.LastId = lastId.toString();
            }

            if (isNotNullOrUndefined(sortProperty)) {
                req.SortBy = sortProperty;
            }

            if (isNotNullOrUndefined(sortDirection)) {
                req.SortDirection = sortDirection;
            } else {
                req.SortDirection = SortDirectionEnum.Undefined;
            }

            this.gatewayServiceV2.Request({
                AccountingLedgerImportGet: req
            }).then(res => {
                if (Object.keys(res.Errors).length > 0) {
                    console.error(res.Errors['AccountingLedgerImportGet']);
                    this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { Err:res.Errors, ToasterMessage: LedgerImportEffectErrorCodes.Load } }));
                    reject();
                }
                let ledgerImports = res.AccountingLedgerImportGet.LedgerImports.map(LedgerImportEntityFromBackendGrpc);
                this.handleLedgerImportResponse(ledgerImports, true);
                resolve({
                    ledgerImports: ledgerImports,
                    pageCount: +res.AccountingLedgerImportGet.PageCount || undefined,
                    nextLastId: +res.AccountingLedgerImportGet.LastId || undefined,
                    nextSortByValue: res.AccountingLedgerImportGet.LastSortByValue,
                });
            }).catch((err) => {
                this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { Err: err, ToasterMessage: LedgerImportEffectErrorCodes.Load } }));
                reject();
            })
        });
        // let requestPayload: MaterialGetPagingRequest;
        // try {
        //
        // } catch (err) {
            // this.store.dispatch(
            //     BaseActionTypes.ErrorAction({
            //         Payload: {
            //             ToasterMessage: ErrorCodes.Load,
            //             Err: err,
            //         },
            //     }),
            // );
            // console.log('catch', err);
            // return firstValueFrom(of({ length: null, items: [], hasError: true, isCountLimitReached: false }));
        // }
    }

    public getLedgerImportsFromCommissions$(commissionIds: number[]): Promise<LedgerImportEntity[]> {
        return new Promise((resolve, reject) => {
            this.gatewayServiceV2.Request({
                AccountingLedgerImportGet: {
                    CommissionIds: commissionIds.map(id => id.toString()),
                    CustomerIds: [],
                    EventIds: [],
                    LedgerImportIds: [],
                    UserIds: [],
                    DocumentTypes: [],
                    DocumentStatus: [],
                }
            }).then((res) => {
                if (res && res.AccountingLedgerImportGet) {
                    let ledgerImports = res.AccountingLedgerImportGet.LedgerImports.map(LedgerImportEntityFromBackendGrpc);
                    this.handleLedgerImportResponse(ledgerImports);
                    resolve(ledgerImports);// todo Observable aus cache zurück geben
                } else {
                    this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Load } }));
                    reject();
                }
            }).catch(() => {
                this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Load } }));
                reject();
            })
        });
    }

    /**
     *
     * @param res response from request
     * @param onlyUpdate only updates existing entries bisher unrelevant da alles im state steht, sobald das nicht mehr der fall ist kann hierdurch die state/cache größe gespart werden
     * @private
     */
    private handleLedgerImportResponse(res: LedgerImportEntity[], onlyUpdate = false) {
        this.store.dispatch(LedgerImportActionTypes.UpdateMany({Payload: res}));
    }
    public get$(payload: Omit<LedgerImportGRPCRequest, 'LastId' | 'Limit'>): Observable<LedgerImportEntity[] | null> {

        // this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Load, Err: err } }));

        const ledgerImports: LedgerImportEntity[] = [];
        let pageCount: number | null = null;
        let PageCounter = 0;

        const getPage = (lastId: string | null): Observable<LedgerImportEntity[]> => this.gatewayServiceV2.Request$({AccountingLedgerImportGet: {...payload, Limit: MAX_ENTITY_LIMIT.toString(), LastId: lastId}}).pipe(
            tap((response) => {
                if (AppGatewayResponseHasError(response)) {
                    throw new Error('AccountingLedgerImportGet failed with errors:' + response.Errors);
                }
                ledgerImports.push(...response.AccountingLedgerImportGet.LedgerImports.map(LedgerImportEntityFromBackendGrpc));
                PageCounter++;
                if (response.AccountingLedgerImportGet.PageCount) {
                    pageCount = +response.AccountingLedgerImportGet.PageCount;
                }
            }),
            switchMap((response) => {
                if (PageCounter < pageCount) {
                    return getPage(response.AccountingLedgerImportGet.LastId);
                }
                return of(ledgerImports);
            }),
            catchError((err) => {
                this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Load, Err: err } }));
                return of(null);
            })
        );
        return getPage(null).pipe(
            tap((v) => console.log('ledgerImports', ledgerImports)),
        );
    }
    public getLedgerImportId$(id: number, forceReload = true): Observable<LedgerImportEntity> {
        return this.getLedgerImportIds$([id], forceReload).pipe(map((entities) => entities && entities[0]));
    }
    public getLedgerImportIds$(ids: number[], forceReload = true): Observable<LedgerImportEntity[]> {
        if (ids.length === 0) {
            return of([]);
        }
        if (ids.some(id => !id)) {
            throw new Error('Invalid id');
        }
        const cache$ = this.selectByIds$(ids);
        const request$ = this.get$({
            LedgerImportIds: ids.map(id => id.toString()),
            CommissionIds: [],
            CustomerIds: [],
            EventIds: [],
            UserIds: [],
            DocumentTypes: [],
            DocumentStatus: [],
        }).pipe(
            tap(v => v && this.handleLedgerImportResponse(v)),
            switchMap(() => cache$),
        );

        if (!forceReload) {
            return cache$.pipe(
                first(),
                switchMap(cache => cache.every(li => li !== undefined) ? cache$ : request$),
            );
        } else {
            return request$;
        }

    }

    public getLedgerImportByEventIds$(eventIds: number[]): Promise<LedgerImportEntity[]> {
        if (!eventIds) {
            throw 'eventId required';
        }
        if (eventIds.length === 0) {
            console.error('no eventIds provided');
            return Promise.resolve([]);
        }
        const queryString = `{
  ledgerImport(eventIds: [${eventIds.join(',')}]) {
    ${LedgerImportEntity.GqlFields}}
    }`;
        return firstValueFrom(
            this.gatewayHttpService.graphQl({ query: queryString }).pipe(
                map((res) => {
                    if (res && res.ledgerImport) {
                        const li = res.ledgerImport.map(LedgerImportEntityFromBackend);
                        this.handleLedgerImportResponse(li);
                        return li; // todo Observable aus cache zurück geben
                    } else {
                        this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Load } }));
                        return null;
                    }
                }),
            ),
        );
    }

    public async  getConsoliadatedInvoiceIdAvailable (Payload: IsConsolidatedInvoiceIdAvailableRequest, textFieldName: string){
        try {
            let res = await this.gatewayServiceV2.Request({ AccountingIsConsolidatedInvoiceIdAvailable: Payload  });
            if (Object.keys(res?.Errors || {}).length === 0) {
                return res.AccountingIsConsolidatedInvoiceIdAvailable.Available;
            } else {
                throw res.Errors;
            }
        } catch (err) {
            this.store.dispatch(
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: textFieldName+' Abrufen fehlgeschlagen',
                        Err: err,
                    },
                }),
            );
        }
    }

    /**
     * @deprecated broken in Safari
     * @param Payload
     */
    getPdfFromHtml$(Payload: HtmlToPdfRequest): Observable<Blob> {
        console.log('getPdfFromHtml$')
        const body = HtmlToPdfRequest.create({
            ...Payload,
        });
        return this.store.select(getToken).pipe(
            first(),
            switchMap((token) => {
                let headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
                let _body:  ArrayBuffer | string;
                if (useGzip()) {
                    headers = headers.set('Accept-Encoding', 'gzip');
                }
                if (window['env'].useProtobuf) {
                    headers = headers.set('Content-Type', 'application/protobuf');
                    _body = HtmlToPdfRequest.encode(body).finish().buffer;
                } else {
                    headers = headers.set('Content-Type', 'application/json');
                    _body = JSON.stringify(body);
                }
                return this.http.post(environment.gatewayApiUrl + '/html-to-pdf/htmlToPdf', _body, {headers, responseType: 'blob'})
            }),
            catchError((e) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: 'Pdf generieren fehlgeschlagen',
                            Err: e,
                        },
                    }),
                );
                return of(null);
            })
        )
    }
    getPdfFromHtml(Payload: HtmlToPdfRequest): Promise<Blob | unknown> {
        console.log('getPdfFromHtml', Payload)

        const body = HtmlToPdfRequest.create({
            ...Payload,
        });
        return firstValueFrom(this.store.select(getToken))
            .then((token) => {
                return new Promise((resolve, reject) => {
                    const req = new XMLHttpRequest();
                    req.onload = (e) => {
                        if (req.status === 200) {
                            console.log(req);
                            console.log(req.response);
                            resolve(req.response);
                        } else {
                            reject(req.response);
                        }
                    };
                    req.onerror = (err) => {
                        reject(err);
                    };
                    req.open('POST', environment.gatewayApiUrl + '/html-to-pdf/htmlToPdf');
                    req.setRequestHeader('Authorization', 'Bearer ' + token);
                    if (window['env']['useGzip']) {
                        req.setRequestHeader('Accept-Encoding', 'gzip');
                    }
                    req.responseType = 'blob';
                    if (window['env'].useProtobuf) {
                        // req.responseType = 'arraybuffer';
                        req.setRequestHeader('Content-Type', 'application/protobuf');
                        req.send(HtmlToPdfRequest.encode(body).finish());
                    } else {
                        // req.responseType = 'text';
                        req.setRequestHeader('Content-Type', 'application/json');
                        req.send(JSON.stringify(body));
                    }
                });
            })
            .catch((e) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: 'Pdf generieren fehlgeschlagen',
                            Err: e,
                        },
                    }),
                );
                return null;
            });
    }

    public async getCurrentPaymentItems(ledgerImportId: number): Promise<LedgerImportPaymentItem[] | null> {
        try {
            const token = await firstValueFrom(this.store.select(getToken));
            const queryString = `mutation {calculatePaymentItems(ledgerImportId: ${ledgerImportId}) {${LedgerImportEntity.PaymentItemGqlFields.join(',')}}}`
            const res = await firstValueFrom(this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: false }));
            if (res?.calculatePaymentItems) {
                return res.calculatePaymentItems.map(LedgerImportPaymentItemFromString);
            } else {
                throw 'Error calculating payment items; wrong response';
            }
        } catch (e) {
            this.store.dispatch(
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: 'Zahlungsdaten abrufen fehlgeschlagen',
                        Err: e,
                    },
                }));
            return null;
        }
    }

    public getLedgerImportDocumentStatus(id: number) {
        const queryString = `{
  ledgerImport(id: ${id}) {
      documentStatus
    }
  }`;
        return firstValueFrom(this.httpService.graphQl({query: queryString}).pipe(
            map((res) => {
                if (res && res.ledgerImport && res.ledgerImport.length === 1) {
                    return res.ledgerImport[0].documentStatus;
                } else {
                    this.store.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: LedgerImportEffectErrorCodes.Load } }));
                    return null;
                }
            }),
        ))
    }
}
